import { useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { clearAnalysisReportsCache } from '../../api/spend';
import { useAuth, useAuthState } from '../../auth/AuthProvider';
import styles from './AppShell.module.css';
import { Button } from '../ui/Button';

function roleLabel(role: 'admin' | 'standard') {
  return role === 'admin' ? 'Admin' : 'Standard';
}

export function AppShell() {
  const auth = useAuth();
  const state = useAuthState();
  const location = useLocation();
  const queryClient = useQueryClient();
  const prevPathnameRef = useRef(location.pathname);
  const isAdmin = state.status === 'authenticated' && state.user.role === 'admin';
  const adminNavActive =
    isAdmin &&
    (location.pathname === '/admin' || location.pathname.startsWith('/master-tables'));

  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;
    const leftAnalysis = prev === '/analysis' && location.pathname !== '/analysis';
    if (leftAnalysis) {
      clearAnalysisReportsCache(queryClient);
    }
  }, [location.pathname, queryClient]);

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link
            to="/builders"
            className={styles.brandLockup}
            title="Operation Battle Plan"
            aria-label="Operation Battle Plan"
          >
            <span className={styles.brandLine1} aria-hidden>
              OPERATION
            </span>
            <span className={styles.brandLine2} aria-hidden>
              BATTLE PLAN
            </span>
          </Link>
          <nav className={styles.nav} aria-label="Main">
            <NavLink to="/builders" className={({ isActive }) => clsx(isActive && styles.active)}>
              Builders
            </NavLink>
            <NavLink to="/suppliers" className={({ isActive }) => clsx(isActive && styles.active)}>
              Suppliers
            </NavLink>
            <NavLink
              to="/analysis"
              className={({ isActive }) => clsx(isActive && styles.active)}
              onMouseEnter={() => {
                void import('../../pages/SpendPage');
              }}
            >
              Analysis
            </NavLink>
            {isAdmin ? (
              <NavLink to="/admin" className={() => clsx(adminNavActive && styles.active)}>
                Admin
              </NavLink>
            ) : null}
          </nav>
        </div>
        <div className={styles.headerRight}>
          {state.status === 'authenticated' ? (
            <div className={styles.userBlock} title={state.user.username}>
              <span className={styles.userEmail}>{state.user.username}</span>
              <span className={styles.userRole}>{roleLabel(state.user.role)}</span>
            </div>
          ) : null}
          <Button variant="ghost" className={styles.logoutBtn} onClick={() => void auth.logout()}>
            Log Out
          </Button>
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}
